import { FC, PropsWithChildren, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { LoadingDots } from "@/components/_molecules/loading-dots/loading-dots";
import { NotFoundView } from "@/components/_molecules/not-found-view";
import { findTeamInviteQueryDocument } from "@/domain/team/hooks/api/team-membership.gql";
import { CurrentUserContextProvider } from "@/domain/user/hooks/use-current-user-context";
import { Workspace } from "@/domain/workspace/models/workspace-model";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { useQuery } from "@tanstack/react-query";

import { AcceptTeamInviteCard } from "../components/accept-invite-team-card";
import { JoinWorkspaceFirstCard } from "../components/join-workspace-first-card";

export function AcceptInviteTeamPage() {
  const { t } = useTranslation("auth");
  useDocumentTitle(t("invite.acceptInvite"));
  const [searchParams] = useSearchParams();

  const token = searchParams.get("token");

  if (!token) {
    return <NotFoundView />;
  }

  return <IsValidTokenInnerPage token={token} />;
}

export type TeamProp = {
  id: string;
  name: string;
};

export type UserProp = {
  id: string;
  email: string;
  fullname?: string | null | undefined;
  username?: string | null | undefined;
  profilePicture?: string | null | undefined;
  activated: boolean;
};

export interface IsValidTokenInnerPageProps extends PropsWithChildren {
  token: string;
}

const IsValidTokenInnerPage: FC<IsValidTokenInnerPageProps> = ({ token }) => {
  const { data, isLoading: loading } = useQuery({
    queryKey: ["findTeamInvite", token],
    queryFn: async () => {
      const client = createOperationClient("findTeamInvite");
      return await client.request(findTeamInviteQueryDocument, { input: token });
    },
  });

  const hasPendingWorkspaceMembership = useMemo(() => {
    if (!data) {
      return false;
    }
    return data.findTeamInvite.pendingWorkspaceMembership;
  }, [data]);

  const user = useMemo(() => {
    if (!data) {
      return null;
    }
    return data.findTeamInvite.user;
  }, [data]);

  const team = useMemo(() => {
    if (!data) {
      return null;
    }
    return data.findTeamInvite.team;
  }, [data]);

  const workspace = useMemo(() => {
    return data?.findTeamInvite?.team?.workspace ?? null;
  }, [data]);

  if (loading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-background text-foreground">
        <LoadingDots color="#808080" />
      </div>
    );
  }

  if (!user) {
    return <NotFoundView />;
  }

  if (!team) {
    return <NotFoundView />;
  }

  if (!workspace) {
    return <NotFoundView />;
  }

  return (
    <InnerPage
      token={token}
      workspace={workspace as Workspace}
      team={team}
      user={user}
      hasPendingWorkspaceMembership={hasPendingWorkspaceMembership}
    />
  );
};

export interface InnerPageProps extends PropsWithChildren {
  token: string;
  workspace: Workspace;
  team: TeamProp;
  user: UserProp;
  hasPendingWorkspaceMembership: boolean;
}

const InnerPage: FC<InnerPageProps> = ({ token, workspace, team, user, hasPendingWorkspaceMembership }) => {
  return (
    <CurrentUserContextProvider>
      <div className="flex h-screen w-screen items-center justify-center bg-background text-foreground">
        {hasPendingWorkspaceMembership && (
          <JoinWorkspaceFirstCard token={token} workspace={workspace} team={team} user={user}></JoinWorkspaceFirstCard>
        )}
        {!hasPendingWorkspaceMembership && (
          <AcceptTeamInviteCard token={token} workspace={workspace} team={team} user={user}></AcceptTeamInviteCard>
        )}
      </div>
    </CurrentUserContextProvider>
  );
};
